import { useState } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Chip,
  Tooltip,
} from "@mui/material"
import {
  MoreVert as MoreVertIcon,
} from "@mui/icons-material"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"

function TaxPending() {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedJob, setSelectedJob] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogType, setDialogType] = useState(null) // "status" | "subStatus"
  const [newStatus, setNewStatus] = useState("")
  const [newSubStatus, setNewSubStatus] = useState("")
  const [comments, setComments] = useState("")
  const [anchorEl, setAnchorEl] = useState(null)
  const [fees,setFees] = useState("")

  const queryClient = useQueryClient();

  const { data: jobs, isLoading } = useApiQuery(["tax-pending"], `/jobs/sub-status/taxPending`,{})

    const updateJobStatusMutation = useApiMutation(`/jobs/${selectedJob?.JobId}/status`, "PATCH", {
      // invalidateQueries: ["tax-pending"], //this invalidates( auto refetches the query that matches the key)
      onSuccess: () => {
        queryClient.invalidateQueries({queryKey:"tax-pending"})
        setDialogOpen(false)
        setComments("")
        setNewStatus("")
        setNewSubStatus("")
        setAnchorEl(null)
      },
    })

    const updateJobSubStatusMutation = useApiMutation(`/jobs/${selectedJob?.JobId}/sub-status`, "PATCH", {
    // invalidateQueries: ["tax-pending"], //this invalidates( auto refetches the query that matches the key)
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey:"tax-pending"})
      setDialogOpen(false)
      setComments("")
      setNewStatus("")
      setNewSubStatus("")
      setAnchorEl(null)
    },
  })

  const setJobStatusAsCompletedMutation = useApiMutation(`/jobs/${selectedJob?.JobId}/status/completed`,"PATCH", {
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey:"tax-pending"});
          setDialogOpen(false)
          setComments("")
          setNewStatus("")
          setNewSubStatus("")
          setAnchorEl(null)
        }
      })

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleMenuClick = (event, job) => {
    setAnchorEl(event.currentTarget)
    setSelectedJob(job)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleOpenDialog = (job, type) => {
    setSelectedJob(job)
    setDialogType(type)
    setDialogOpen(true)
    setAnchorEl(null)
  }

  const handleConfirmUpdate = () => {
    let data = {}
    if (dialogType === "status") {
      data = { 
        status: newStatus,
        reason: comments || "",
      }
      if (newStatus === "completed") {
        data.fees = fees
        setJobStatusAsCompletedMutation.mutate(data)
      } else {
        updateJobStatusMutation.mutate(data)
      }

    } else if (dialogType === "subStatus") {
      data = { 
        subStatus: newSubStatus,
        reason: comments || "",
        }
        updateJobSubStatusMutation.mutate(data)
    }
  }

  if (isLoading) {
    return <div>Loading....</div>
  }

  const displayJobs = jobs
  const paginatedJobs = displayJobs?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        All Processing Jobs
      </Typography>
      <Typography variant="body1" color="textSecondary" paragraph>
        These are all accepted jobs.
      </Typography>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Job Type</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sub Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedJobs.map((job) => (
                <TableRow key={job.JobId} hover>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
                      {job.JobId}
                    </Typography>
                  </TableCell>
                  <TableCell>{job.clientName}</TableCell>
                  <TableCell>{job.jobType}</TableCell>
                  <TableCell>{new Date(job.dueDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={job.status}
                      color={
                        job.status === "completed"
                          ? "success"
                          : job.status === "processing"
                          ? "primary"
                          : "default"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {job.subStatus ? (
                      <Chip label={job.subStatus} color="warning" size="small" />
                    ) : (
                      <Chip label="None" variant="outlined" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="More actions">
                      <IconButton onClick={(e) => handleMenuClick(e, job)} size="small">
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && selectedJob?.JobId === job.JobId}
                      onClose={handleMenuClose}
                    >
                      {job.status === "new" && (
                        <MenuItem onClick={() => updateJobStatusMutation.mutate({ status: "processing" })}>
                          Accept Job
                        </MenuItem>
                      )}
                      <MenuItem onClick={() => handleOpenDialog(job, "status")}>Set Status</MenuItem>
                      <MenuItem onClick={() => handleOpenDialog(job, "subStatus")}>Set Sub Status</MenuItem>
                      {/* <MenuItem onClick={() => updateJobMutation.mutate({ status: "completed" })}>
                        Mark as Completed
                      </MenuItem> */}
                      <MenuItem onClick={() => handleOpenDialog(job, "comment")}>Add Comment</MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedJobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No jobs in this status
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={displayJobs?.length || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>

      {/* Dialog for Status or Sub Status */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogType === "status"
            ? "Update Job Status"
            : dialogType === "subStatus"
            ? "Update Job Sub Status"
            : "Add Comment"}
        </DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob.jobType}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob.clientName}
              </Typography>

              {dialogType === "status" && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>New Status</InputLabel>
                  <Select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    label="New Status"
                  >
                    {/* <MenuItem value="new">New</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem> */}
                    <MenuItem value="cancelled">Canceled By Client</MenuItem>

                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              )}

              {newStatus === "completed" && (
                <TextField
                  fullWidth
                  label="Fees"
                  type="number"
                  value={fees}
                  onChange={(e) => setFees(e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}

              {dialogType === "subStatus" && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>New Sub Status</InputLabel>
                  <Select
                    value={newSubStatus}
                    onChange={(e) => setNewSubStatus(e.target.value)}
                    label="New Sub Status"
                  >
                    <MenuItem value="documentsPending">Documents Pending</MenuItem>
                    <MenuItem value="taxPending">Tax Pending</MenuItem>
                    <MenuItem value="verificationPending">Verification Pending</MenuItem>
                    <MenuItem value="clientNotResponding">Client Not Responding</MenuItem>
                    <MenuItem value="keptOnHold">Kept on Hold</MenuItem>
                    <MenuItem value="none">None</MenuItem>
                  </Select>
                </FormControl>
              )}

              {(dialogType === "status" || dialogType === "subStatus" || dialogType === "comment") && (
                <TextField
                  fullWidth
                  label="Comments"
                  multiline
                  rows={3}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Add notes about the update..."
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            disabled={
              (dialogType === "status" && (!newStatus || (newStatus === "completed" && !fees))) ||
              (dialogType === "subStatus" && !newSubStatus)
          }>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TaxPending
